// ** React Imports **
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** React-Hook-Form **
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** MUI Components (only Dialog and IconButton) **
import {
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";

// ** Redux **
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "src/redux/actions/AuthAction";

// ** Context **
import { useAuthModal } from "src/context/AuthModalContext";

// ** Constants **
import { FormValidationMessages } from "src/constants/form.const";

// ** Snackbar **
import { useSnackbar } from 'notistack';

// ** Components **
import InputField from "../authentication/InputField";
import LoadingOverlay from "../authentication/LoadingOverlay";

// ** Styles **
import './LoginModal.css';

/**
 * ResetPasswordModal Component
 * 
 * Modal popup for resetting password
 * Matches the design of LoginModal
 */

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .min(6, "password must be between 6-12 characters")
    .max(12, "password must be between 6-12 characters")
    .required(FormValidationMessages.PASSWORD.required),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], "passwords must match")
    .required("confirm password is required"),
});

const ResetPasswordModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader } = useSelector((state) => state.auth);
  
  // Auth modal context
  const { 
    isResetPasswordModalOpen, 
    closeModals,
  } = useAuthModal();

  // Local state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isResetPasswordModalOpen) {
      reset();
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isResetPasswordModalOpen, reset]);

  /**
   * Handle reset password form submission
   */
  const onSubmit = async (data) => {
    try {
      const result = await dispatch(resetPassword({ password: data.password }));
      
      if (result.type === "auth/resetPassword/fulfilled") {
        enqueueSnackbar("Password reset successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 2000,
        });
        closeModals();
        navigate("/home");
      } else {
        enqueueSnackbar(result.payload || "Failed to reset password", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 2000,
        });
      }
    } catch (error) {
      console.error("Reset password error:", error);
      enqueueSnackbar("Something went wrong. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (!loadingBtnLoader) {
      closeModals();
    }
  };

  const formValues = watch();

  return (
    <Dialog
      open={isResetPasswordModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: 480,
          backgroundColor: 'white',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '3rem 2.5rem' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loadingBtnLoader}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Loading Overlay */}
        <LoadingOverlay isVisible={loadingBtnLoader} type="resetPassword" />

        {/* Card Title */}
        <h2 className="login-page-new__title">reset password</h2>

        <p style={{ 
          textAlign: 'center', 
          color: '#666', 
          marginBottom: '2rem',
          fontSize: '0.938rem',
          lineHeight: '1.5'
        }}>
          your new password should be different to the previous one
        </p>

        {/* Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="login-page-new__form">
          
          {/* Password Input */}
          <div className="login-page-new__password-wrapper">
            <InputField
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder="create new password*"
              value={formValues.password}
              error={errors.password ? errors.password.message : ''}
              required
            />
            <button
              type="button"
              className="login-page-new__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label="toggle password visibility"
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Confirm Password Input */}
          <div className="login-page-new__password-wrapper">
            <InputField
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="confirm new password*"
              value={formValues.confirmPassword}
              error={errors.confirmPassword ? errors.confirmPassword.message : ''}
              required
            />
            <button
              type="button"
              className="login-page-new__password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label="toggle confirm password visibility"
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="login-page-new__submit-btn"
            disabled={loadingBtnLoader}
          >
            {loadingBtnLoader ? 'resetting...' : 'reset password'}
          </button>
        </form>
      </div>
    </Dialog>
  );
};

export default ResetPasswordModal;
